import React from "react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { ArrowUpRight, Search, ArrowUpDown, Users, CheckCircle, CalendarX, FileText, X, Check } from "lucide-react";
import { useHR } from "../context/HRContext";
import { MONTHS, ATTENDANCE_STATUS_LABELS, ATTENDANCE_STATUS_COLORS, getDepartmentLabel } from "../constants/hrConstants";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import StatCard from "@/components/ui/StatCard";
import DonutWithLegend from "@/components/ui/DonutWithLegend";
import FormInput from "@/components/ui/FormInput";
import FormSelect from "@/components/ui/FormSelect";
import { Button } from "@/components/ui/Button";

const ATT_COLORS = ["#10B981", "#EF4444", "#F59E0B", "#3B82F6", "#8B5CF6"];
const DEPT_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#6366F1", "#EC4899", "#8B5CF6"];

const ATTENDANCE_STATUS_CONFIG = [
  { key: 'present', label: 'Present', icon: CheckCircle, color: '#10B981', bg: '#ECFDF5' },
  { key: 'absent', label: 'Absent', icon: X, color: '#EF4444', bg: '#FEF2F2' },
  { key: 'late', label: 'Late', icon: ArrowUpRight, color: '#F59E0B', bg: '#FFFAEB' },
  { key: 'half_day', label: 'Half Day', icon: CalendarX, color: '#3B82F6', bg: '#EFF6FF' },
]

const ATT_HEX_COLORS = Object.fromEntries(ATTENDANCE_STATUS_CONFIG.map(s => [s.key, s.color]));

const HRDashboard = () => {
  const navigate = useNavigate();
  const { employees, attendance, leaves, payslips, updateLeaveStatus, dashboardStats, loadingStats } = useHR();

  const today = new Date().toISOString().split("T")[0];

  const [searchQuery, setSearchQuery] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState("");
  const [sortOrder, setSortOrder] = React.useState("default");

  const handleUpdateStatus = async (id, status) => {
    try {
      await updateLeaveStatus(id, status, `${status.charAt(0).toUpperCase() + status.slice(1)} via Dashboard`);
      toast.success(`Leave request ${status} successfully!`);
    } catch (err) {
      toast.error("Failed to update leave request status.");
    }
  };

  const onLeaveEmployeeIds = React.useMemo(() => {
    const ids = new Set();
    leaves.forEach((l) => {
      if (l.status === "approved" && new Date(l.fromDate) <= new Date(today) && new Date(l.toDate) >= new Date(today)) {
        ids.add(l.employeeId);
      }
    });
    return ids;
  }, [leaves, today]);

  const processedAtt = React.useMemo(() => {
    let records = attendance.filter((a) => a.date === today && !onLeaveEmployeeIds.has(a.employeeId));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      records = records.filter((r) => r.employeeName?.toLowerCase().includes(q) || r.employeeId?.toLowerCase().includes(q));
    }
    if (filterStatus) {
      records = records.filter((r) => {
        const status = r.status || (r.checkIn ? "present" : "absent");
        return status === filterStatus;
      });
    }
    if (sortOrder === "asc") records = [...records].sort((a, b) => (a.employeeName || "").localeCompare(b.employeeName || ""));
    else if (sortOrder === "desc") records = [...records].sort((a, b) => (b.employeeName || "").localeCompare(a.employeeName || ""));
    return records.slice(0, 7);
  }, [attendance, today, searchQuery, filterStatus, sortOrder, onLeaveEmployeeIds]);

  const recentLeaves = leaves.filter((l) => l.status === "pending").slice(0, 4);
  const recentPayslips = payslips.slice(0, 4);

  const totalEmployees = employees.length;
  const presentToday = attendance.filter(a => a.date === today && (a.status === "present" || a.checkIn)).length;
  const onLeaveToday = leaves.filter(l => l.status === "approved" && new Date(l.fromDate) <= new Date(today) && new Date(l.toDate) >= new Date(today)).length;
  const pendingLeaves = leaves.filter(l => l.status === "pending").length;

  const totalAttendanceToday = attendance.filter(a => a.date === today && !onLeaveEmployeeIds.has(a.employeeId)).length;

  const employeeDeptData = React.useMemo(() => {
    const deptMap = {};
    employees.forEach(e => {
      const dept = getDepartmentLabel(e.department);
      deptMap[dept] = (deptMap[dept] || 0) + 1;
    });
    return Object.entries(deptMap).map(([name, value]) => ({ name, value }));
  }, [employees]);

  const attendanceStatusData = React.useMemo(() => {
    const statusMap = { present: 0, absent: 0, late: 0, half_day: 0 };
    attendance.filter(a => a.date === today && !onLeaveEmployeeIds.has(a.employeeId)).forEach(a => {
      const status = a.status || (a.checkIn ? "present" : "absent");
      if (statusMap[status] !== undefined) statusMap[status]++;
    });
    return Object.entries(statusMap).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  }, [attendance, today, onLeaveEmployeeIds]);

  if (loadingStats || !dashboardStats) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-slate-800"></div>
        <p className="text-sm font-medium text-slate-500">Loading dashboard statistics...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-2 overflow-x-hidden">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-2xl text-slate-900 tracking-wide">HR Dashboard</h1>
          <p className="text-xs text-slate-400 mt-1 font-medium">
            {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard variant="luxury" title="Total Employees" value={totalEmployees} topColor="border-t-blue-500" iconHoverColor="group-hover:text-blue-500" illustration={<Users className="w-10 h-10" strokeWidth={1.5} />} />
        <StatCard variant="luxury" title="Present Today" value={presentToday} topColor="border-t-emerald-500" iconHoverColor="group-hover:text-emerald-500" illustration={<CheckCircle className="w-10 h-10" strokeWidth={1.5} />} />
        <StatCard variant="luxury" title="On Leave" value={onLeaveToday} topColor="border-t-amber-500" iconHoverColor="group-hover:text-amber-500" illustration={<CalendarX className="w-10 h-10" strokeWidth={1.5} />} />
        <StatCard variant="luxury" title="Pending Leaves" value={pendingLeaves} topColor="border-t-blue-500" iconHoverColor="group-hover:text-blue-500" illustration={<FileText className="w-10 h-10" strokeWidth={1.5} />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Row 1: Charts (Side-by-Side) */}
        <div className="lg:col-span-6 flex flex-col">
          <div className="card p-5 border border-slate-150 rounded-xl bg-white shadow-sm flex flex-col h-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-lg text-slate-800 font-semibold">Employee Distribution</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate("/hr/onboarding")}>
                <ArrowUpRight size={14} className="text-slate-600" />
              </Button>
            </div>
            <div className="flex-1 flex flex-col items-center gap-6 sm:flex-row sm:items-center sm:justify-center">
              <div className="relative w-44 h-44 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={employeeDeptData.length > 0 ? employeeDeptData : [{ name: "No Data", value: 1 }]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={68}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {employeeDeptData.map((_, index) => (
                        <Cell key={index} fill={DEPT_COLORS[index % DEPT_COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-3xl font-bold text-slate-800 font-display leading-none">{totalEmployees}</span>
                  <span className="text-xs text-slate-400 mt-1">Employees</span>
                </div>
              </div>
              <div className="w-full sm:w-auto flex-1 flex flex-col gap-1.5 min-w-0">
                {employeeDeptData.length > 0 ? (
                  employeeDeptData.map((d, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: DEPT_COLORS[i % DEPT_COLORS.length] }}></span>
                      <span className="text-slate-600 truncate flex-1">{d.name}</span>
                      <span className="font-semibold text-slate-800 text-right w-10 tabular-nums">{d.value}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400 text-center">No employee data</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 flex flex-col">
          <div className="card border border-slate-150 rounded-xl bg-white shadow-sm flex flex-col h-full">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-display text-lg text-slate-800 font-semibold">Attendance Status Today</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate("/hr/attendance")} className="text-slate-600 hover:text-slate-900">
                View All <ArrowUpRight size={12} className="ml-1" />
              </Button>
            </div>
            <div className="flex-1 p-5">
              <div className="flex flex-col lg:flex-row items-center gap-6">
                {/* Donut Chart */}
                <div className="relative w-48 h-48 lg:w-56 lg:h-56 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={attendanceStatusData.length > 0 ? attendanceStatusData : [{ name: "No Data", value: 1 }]}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={78}
                        paddingAngle={3}
                        stroke="none"
                      >
                        {attendanceStatusData.map((d, index) => (
                          <Cell key={index} fill={ATT_HEX_COLORS[d.name] || ATT_COLORS[index % ATT_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-3xl font-bold text-slate-800 font-display leading-none">{totalAttendanceToday}</span>
                    <span className="text-[10px] text-slate-400 mt-0.5">Total Today</span>
                  </div>
                </div>

                {/* Status List */}
                <div className="w-full lg:flex-1 flex flex-col gap-2.5 min-w-0">
                  {ATTENDANCE_STATUS_CONFIG.map((s) => {
                    const data = attendanceStatusData.find(d => d.name === s.key);
                    const count = data?.value || 0;
                    const pct = totalAttendanceToday > 0 ? ((count / totalAttendanceToday) * 100).toFixed(1) : 0;
                    const Icon = s.icon;
                    return (
                      <div key={s.key} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-50/50 hover:bg-slate-100/50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }}></span>
                          <Icon className="w-4 h-4 flex-shrink-0" style={{ color: s.color }} />
                          <span className="text-sm font-medium text-slate-600">{s.label}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden flex-shrink-0">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: s.color }}></div>
                          </div>
                          <span className="text-sm font-bold text-slate-800 w-6 text-right tabular-nums">{count}</span>
                          <span className="text-xs text-slate-400 w-10 text-right tabular-nums">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                  {totalAttendanceToday === 0 && (
                    <p className="text-sm text-slate-400 text-center py-6">No attendance records for today</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Details & Actions */}
        <div className="lg:col-span-7 flex flex-col">
          <div className="card p-5 border border-slate-150 rounded-xl bg-white shadow-sm flex-1 flex flex-col min-h-[420px] h-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-lg text-slate-800 font-semibold">Attendance Detail</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate("/hr/attendance")}>
                <ArrowUpRight size={14} className="text-slate-600" />
              </Button>
            </div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <FormInput
                  placeholder="Search name/ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <FormSelect
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                options={[
                  { value: "", label: "All Statuses" },
                  { value: "present", label: "Present" },
                  { value: "absent", label: "Absent" },
                  { value: "half_day", label: "Half Day" },
                  { value: "late", label: "Late" },
                ]}
                className="w-[150px]"
              />
              <Button
                variant={sortOrder !== 'default' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : prev === 'desc' ? 'default' : 'asc')}
                className="gap-1.5"
              >
                Sort {sortOrder === 'asc' ? 'A-Z' : sortOrder === 'desc' ? 'Z-A' : ''}
                <ArrowUpDown size={12} />
              </Button>
            </div>
            <div className="flex-1 overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Employee</th>
                    <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Emp ID</th>
                    <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Check In</th>
                    <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Check Out</th>
                    <th className="pb-3 text-xs font-semibold text-slate-400 uppercase tracking-wider text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-slate-50">
                  {processedAtt.length === 0 ? (
                    <tr><td colSpan="5" className="py-8 text-center text-slate-400 text-xs">No matching records found.</td></tr>
                  ) : (
                    processedAtt.map((att, idx) => {
                      const derivedStatus = att.status || (att.checkIn ? "present" : "absent");
                      return (
                        <tr key={att.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-3">
                            <p className="font-semibold text-slate-800 text-xs">{att.employeeName}</p>
                          </td>
                          <td className="py-3">
                            <span className="text-[11px] font-medium text-slate-500 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded">{att.employeeId}</span>
                          </td>
                          <td className="py-3 text-xs font-medium text-slate-700">{att.checkIn || "--:--"}</td>
                          <td className="py-3 text-xs font-medium text-slate-700">{att.checkOut || "--:--"}</td>
                          <td className="py-3 text-center">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ATTENDANCE_STATUS_COLORS[derivedStatus] || "bg-slate-100 text-slate-500"}`}>
                              {ATTENDANCE_STATUS_LABELS[derivedStatus] || derivedStatus}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 flex flex-col gap-6">
          <div className="card p-5 border border-slate-150 rounded-xl bg-white shadow-sm flex-1 flex flex-col min-h-[260px]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display text-lg text-slate-800 font-semibold">Recent Payroll</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate("/hr/payslips")}>
                <ArrowUpRight size={14} className="text-slate-600" />
              </Button>
            </div>
            <div className="flex flex-col gap-4 overflow-y-auto pr-2 flex-1">
              {recentPayslips.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No recent payrolls.</p>
              ) : (
                recentPayslips.map((p, idx) => (
                  <div key={p.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {p.employeeName}
                          <span className="text-[11px] font-normal text-slate-400 ml-2">#{p.employeeId}</span>
                        </p>
                        <p className="text-[11px] text-slate-400">
                          {(() => { const monthIdx = parseInt(p.month, 10) - 1; const monthName = MONTHS[monthIdx] || p.month || ""; return `${monthName} ${p.year || ""}`.trim() || "N/A"; })()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-800">₹{p.netPay.toLocaleString()}</p>
                      <p className={`text-[11px] font-medium ${p.status === 'paid' ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {p.status === 'paid' ? 'Success' : 'Pending'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="card p-5 border border-slate-150 rounded-xl bg-white shadow-sm flex-1 flex flex-col min-h-[260px]">
            <h3 className="font-display text-lg text-slate-800 font-semibold mb-4">Dayoff Request</h3>
            <div className="flex flex-col gap-4 overflow-y-auto pr-2 flex-1">
              {recentLeaves.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No pending requests.</p>
              ) : (
                recentLeaves.map((l, idx) => (
                  <div key={l.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {l.employeeName}
                          <span className="text-[11px] font-normal text-slate-400 ml-2">#{l.employeeId}</span>
                        </p>
                        <p className="text-[11px] text-slate-400 capitalize">{l.leaveType} Leave</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleUpdateStatus(l.id || l._id, "rejected")} 
                        className="flex items-center justify-center h-8 w-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white border border-rose-100 transition-all duration-200 shadow-sm"
                        title="Reject Request"
                      >
                        <X size={15} />
                      </button>
                      <button 
                        onClick={() => handleUpdateStatus(l.id || l._id, "approved")} 
                        className="flex items-center justify-center h-8 w-8 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white border border-emerald-100 transition-all duration-200 shadow-sm"
                        title="Approve Request"
                      >
                        <Check size={15} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRDashboard;
